import $ from 'jquery';
import layout from '../Layout';
import getHeaderOffset from '../helpers/getHeaderOffset';

export default class StickyHeader {
  constructor(options, settings) {
    this.postMessage = options.postMessage;
    this.$body = $(document.body);
    this.$window = $(window);
    this.$siteMain = $('.site-main');

    this.$el = options.$el;
    this.$toggle = options.$toggle;
    this.settings = settings;

    this.stickyClass = 'site-header-sticky';
    this.scrolledClass = 'site-header-sticky--scrolled';
    this.navOpenClass = 'site-header-sticky--open';

    this.$announcementBar = this.$el.find('.site-announcement');
    this.$header = options.$header;
    this.header = this.$header[0];

    if (!this.settings.sticky_header) {
      return this._stickyReset();
    }

    // Revert navigation to original state on breakpoint change
    this.navVisibility = this.navVisibility.bind(this);
    layout.onBreakpointChange(this.navVisibility);

    this.stickyOffset = this.stickyOffset.bind(this);
    window.addEventListener('resize', () => this.stickyOffset());

    this._toggleStickyHeader = this._toggleStickyHeader.bind(this);
    this._headerChecker = this._headerChecker.bind(this);

    this.slideToggle = this._slideToggle.bind(this);

    this._init();

    this.stickyOffset();

    // We were experiencing an issue with fonts loading after the header had
    // already been resized, which was causing an overlap or a gap between the
    // header and the main content. This will fix chrome and firefox, but we would
    // need to add a polyfill for IE/Edge to work.
    if (document.fonts) {
      document.fonts.ready.then(() => this.stickyOffset());
    }
  }

  /**
   * Re-fetch the announcement bar element, and re-position the header.
   * @see See the StaticAnnouncement class for details.
   */
  updateAnnouncement() {
    this.$announcementBar = this.$el.find('.site-announcement');

    if (this.settings.sticky_header) {
      this._toggleAnnouncementBar(this.$body.hasClass(this.scrolledClass));
      this._headerChecker();
    }
  }

  closeNavigation() {
    if (!this.settings.sticky_header) return;

    this.$header.removeClass(this.navOpenClass).one('trend', () => {
      this.$body.trigger('position-banners');
    });
    this.postMessage('nav:close-all');
  }

  openNavigation() {
    this.$header.addClass(this.navOpenClass).one('trend', () => {
      this.$body.trigger('position-banners');
    });
  }

  navVisibility(event, breakpoints) {
    if (breakpoints.previous === 'L') this.closeNavigation();
  }

  unload() {
    if (!this.settings.sticky_header) return;

    this.$body.removeClass(this.stickyClass).removeClass(this.scrolledClass);

    this.$toggle.off('.sticky-header');
    this.$window.off('.sticky-header');

    layout.offBreakpointChange(this.navVisibility);
    layout.offBreakpointChange(this.stickyOffset);

    if (this.requestAnimationFrameId) {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    }
  }

  _init() {
    this.stickyOffset(true);
    this._bindEvents();

    if (window.requestAnimationFrame) {
      this._headerChecker();
    }
  }

  _bindEvents() {
    this.$toggle.on('click.sticky-header', (event) => {
      event.preventDefault();

      if (layout.isBreakpoint('L')) this._slideToggle();
    });

    this.$window.on('scroll.sticky-header', this._toggleStickyHeader);
  }

  _stickyReset() {
    this.$siteMain.css('paddingTop', '');
  }

  /**
   * Determine the offset when breakpoints change
   *  - Offset is used to determine scroll threshold
   *  - Offset is used to set a body offset on browsers that don't support Sticky
   *
   * @param isInit
   * @private
   */
  stickyOffset(isInit) {
    if (!this.settings.sticky_header) return;
    if (this.$body.hasClass(this.scrolledClass)) return;

    if (!this.$body.hasClass(this.stickyClass)) {
      this.$body.addClass(this.stickyClass);
    }

    const offset = getHeaderOffset(this.$el[0]);

    this.$siteMain.css('paddingTop', offset);

    if (isInit) {
      this._toggleStickyHeader();
    }
  }

  /**
   * Open or close the navigation
   *
   * @param open
   * @private
   */
  _slideToggle(open = false) {
    if (this.$header.hasClass(this.navOpenClass) && !open) {
      this.closeNavigation();
      this.$toggle.removeClass('active');
    } else {
      this.openNavigation();
      this.$toggle.addClass('active');
    }
  }

  /**
   *
   *
   * @param hide
   * @private
   */
  _toggleAnnouncementBar(hide = false) {
    if (!this.$announcementBar.length) {
      return;
    }

    let top = 0;

    if (hide) {
      const mobileOnly = this.$announcementBar.hasClass('only-mobile');

      if ((layout.isBreakpoint('XS') && mobileOnly) || !mobileOnly) {
        top = this.$announcementBar.outerHeight() * -1;
      }
    }

    this.$el.css('top', top).one('trend', () => {
      this.$body.trigger('position-banners');
    });
  }

  /**
   * Sticky header only shows as sticky after scroll
   *
   * @private
   */
  _toggleStickyHeader() {
    if (this.$body.hasClass('scroll-lock')) {
      return;
    }

    const scrollThreshold = getHeaderOffset(this.$el[0]) + 20; // Extend threshold 20px past header
    const shouldShrink = window.pageYOffset > scrollThreshold;

    // Sticky header is scrolled, is and is visible -- nothing more to do!
    if (shouldShrink && this.$body.hasClass(this.scrolledClass)) {
      return;
    }

    if (shouldShrink) {
      this._shrink();
      this._toggleAnnouncementBar(true);
    } else {
      this._expand();
      this._toggleAnnouncementBar(false);
    }
  }

  _shrink() {
    this.closeNavigation();
    this.$toggle.removeClass('active');

    this.$header
      .revealer('show')
      .one('revealer-animating.sticky-header', () => {
        this.$body.addClass(this.scrolledClass).one('trend', () => {
          this.$body.trigger('position-banners');
        });
      });
  }

  _expand() {
    this.openNavigation();

    this.$header
      .revealer('hide')
      .one('revealer-animating.sticky-header', () => {
        this.$body.removeClass(this.scrolledClass).one('trend', () => {
          this.$body.trigger('position-banners');
        });
      })
      .one('revealer-hide.sticky-header', () => {
        this.$toggle.removeClass('active');
      });
  }

  _headerChecker() {
    // Periodically check to make sure header is offset correctly
    //this.requestAnimationFrameId = window.requestAnimationFrame(this._headerChecker);

    if (!this.$body.hasClass(this.scrolledClass)) {
      this.stickyOffset();
    }
  }
}
